import { l as DocContext, k as jsxRuntimeExports } from "./doenetml-drNYjJiF.js";
import React__default, { useContext, useRef } from "react";
import { j as jxg, h as syncLineFamilyVisibility, s as syncLayer, e as syncLineStrokeStyle, a as syncWithLabelToggle, f as syncLabelStrokeColor, b as syncLabelMaskCssStyle, i as applyLineFamilyLabelPlacement, k as buildLineFamilyLabelAttributes, d as attachLabelHoverHighlight, c as computeLabelMaskCssStyle, l as stabilizeInitialLineFamilyLabelPlacement, r as removeJXGEventHandlers } from "./JSXGraphRenderer-BcR36ozd.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-CDZbHBy-.js";
import { BoardContext, LINE_LAYER_OFFSET } from "./graph-uNmxqd5I.js";
import { a as buildLineLikeAttributes } from "./buildGraphicalAttributes-C7PtyKL7.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { p as pointerEventToUserCoords } from "./dragThreshold-DuSSFrIU.js";
import { a as resolveLineColor } from "./styleColors-Cknqcr0q.js";
import { s as styleToDash } from "./styleToDash-DWb71gbd.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
import { a as attachLineFamilyDragHandlers } from "./lineFamilyDragHandlers-tcr76K44.js";
const ray = React__default.memo(function Ray(props) {
  let { componentIdx, id, SVs, actions, callAction } = useDoenetRenderer(props);
  Ray.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  let rayJXG = useRef(null);
  const dragState = usePointerDragState();
  const dragCoordination = {
    draggedTag: useRef(null),
    downOnTag: useRef(null)
  };
  let previousWithLabel = useRef(null);
  let cancelInitialLabelPlacement = useRef(null);
  let pointCoords = useRef(null);
  const {
    lastPositionFromCore: lastEndpointFromCore,
    fixed,
    fixLocation
  } = useDraggableRefs(SVs, SVs.numericalEndpoint);
  let lastThroughpointFromCore = useRef(null);
  lastThroughpointFromCore.current = SVs.numericalThroughpoint;
  const { darkMode } = useContext(DocContext) || {};
  useBoardPointerTracking(board, dragState);
  useJSXGraphCleanup({
    objectRef: rayJXG,
    destroy: () => deleteRayJXG(),
    cancelLabelPlacementRef: cancelInitialLabelPlacement
  });
  function createRayJXG() {
    if (board === null) {
      return null;
    }
    if (SVs.numericalEndpoint.length !== 2 || SVs.numericalThroughpoint.length !== 2) {
      rayJXG.current = null;
      return;
    }
    const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
    var jsxRayAttributes = {
      ...buildLineLikeAttributes({
        SVs,
        layerOffset: LINE_LAYER_OFFSET,
        fixed: fixed.current,
        fixLocation: fixLocation.current,
        darkMode
      }),
      straightFirst: false
    };
    jsxRayAttributes.label = buildLineFamilyLabelAttributes({
      labelForGraph: SVs.labelForGraph,
      labelPosition: SVs.labelPosition,
      labelHasLatex: SVs.labelHasLatex,
      applyStyleToLabel: SVs.applyStyleToLabel,
      lineColor,
      layer: SVs.layer,
      maskLabel: SVs.maskLabel
    });
    let through = [
      [...SVs.numericalEndpoint],
      [...SVs.numericalThroughpoint]
    ];
    cancelInitialLabelPlacement.current?.();
    cancelInitialLabelPlacement.current = null;
    let newRayJXG = board.create(
      "line",
      through,
      jsxRayAttributes
    );
    newRayJXG.isDraggable = !fixLocation.current;
    attachLineFamilyDragHandlers({
      jxg: newRayJXG,
      tag: 0,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      actions: {
        move: actions.moveRay,
        focus: actions.rayFocused,
        click: actions.rayClicked
      },
      snapshot: () => [
        [...newRayJXG.point1.coords.scrCoords],
        [...newRayJXG.point2.coords.scrCoords]
      ],
      buildTransientMoveArgs: (e, snap) => {
        const next = [];
        if (e.type === "pointermove" && dragState.pointerAtDown.current && snap) {
          for (let i = 0; i < 2; i++) {
            next.push(
              pointerEventToUserCoords(
                e,
                dragState.pointerAtDown.current,
                snap[i],
                board
              )
            );
          }
        } else {
          next.push([newRayJXG.point1.X(), newRayJXG.point1.Y()]);
          next.push([newRayJXG.point2.X(), newRayJXG.point2.Y()]);
        }
        pointCoords.current = next;
        return {
          endpointcoords: next[0],
          throughcoords: next[1],
          transient: true,
          skippable: true
        };
      },
      buildCommitMoveArgs: (_, variant) => {
        if (variant === "up") {
          return {
            endpointcoords: pointCoords.current?.[0],
            throughcoords: pointCoords.current?.[1]
          };
        }
        return {
          point1coords: pointCoords.current?.[0],
          point2coords: pointCoords.current?.[1]
        };
      },
      onDragApplied: () => {
        newRayJXG.point1.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          lastEndpointFromCore.current
        );
        newRayJXG.point2.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          lastThroughpointFromCore.current
        );
      }
    });
    rayJXG.current = newRayJXG;
    attachLabelHoverHighlight({
      hoverTargetJXG: newRayJXG,
      getLabelJXG: () => rayJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    if (SVs.labelForGraph !== "" && newRayJXG.hasLabel) {
      cancelInitialLabelPlacement.current = stabilizeInitialLineFamilyLabelPlacement({
        board,
        lineLike: newRayJXG,
        applyPlacement: (forceFullUpdate) => {
          if (rayJXG.current !== newRayJXG || !newRayJXG.hasLabel) {
            return false;
          }
          applyLineFamilyLabelPlacement({
            board,
            lineLike: newRayJXG,
            labelPosition: SVs.labelPosition,
            forceFullUpdate,
            setNeedsUpdateOnNoChange: true
          });
          return true;
        }
      });
    }
    previousWithLabel.current = SVs.labelForGraph !== "";
  }
  function deleteRayJXG() {
    cancelInitialLabelPlacement.current?.();
    cancelInitialLabelPlacement.current = null;
    if (!rayJXG.current) {
      return;
    }
    removeJXGEventHandlers(rayJXG.current);
    board?.removeObject(rayJXG.current);
    rayJXG.current = null;
  }
  if (board) {
    if (rayJXG.current === null) {
      createRayJXG();
    } else if (SVs.numericalEndpoint.length !== 2 || SVs.numericalThroughpoint.length !== 2) {
      deleteRayJXG();
    } else {
      let validCoords = true;
      for (let coords of [
        SVs.numericalEndpoint,
        SVs.numericalThroughpoint
      ]) {
        if (!Number.isFinite(coords[0])) {
          validCoords = false;
        }
        if (!Number.isFinite(coords[1])) {
          validCoords = false;
        }
      }
      rayJXG.current.point1.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        SVs.numericalEndpoint
      );
      rayJXG.current.point2.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        SVs.numericalThroughpoint
      );
      let visible = !SVs.hidden;
      syncLineFamilyVisibility(rayJXG.current, visible, validCoords);
      rayJXG.current.visProp.fixed = fixed.current;
      rayJXG.current.visProp.highlight = !fixLocation.current;
      rayJXG.current.isDraggable = !fixLocation.current;
      syncLayer(rayJXG.current, SVs.layer, LINE_LAYER_OFFSET);
      const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
      syncLineStrokeStyle(rayJXG.current, {
        lineColor,
        lineWidth: SVs.selectedStyle.lineWidth,
        lineOpacity: SVs.selectedStyle.lineOpacity,
        dash: styleToDash(SVs.selectedStyle.lineStyle)
      });
      rayJXG.current.name = SVs.labelForGraph;
      syncWithLabelToggle(
        rayJXG.current,
        SVs.labelForGraph,
        previousWithLabel
      );
      rayJXG.current.needsUpdate = true;
      rayJXG.current.update();
      if (rayJXG.current.hasLabel && rayJXG.current.label) {
        const label = rayJXG.current.label;
        syncLabelStrokeColor(label, SVs.applyStyleToLabel, lineColor);
        syncLabelMaskCssStyle(label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
        applyLineFamilyLabelPlacement({
          board,
          lineLike: rayJXG.current,
          labelPosition: SVs.labelPosition,
          setNeedsUpdateOnNoChange: true
        });
      }
      board.updateRenderer();
    }
  }
  if (SVs.hidden) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
});
export {
  ray as default
};
